import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {API_ENDPOINTS} from "../../api/endpoints";
import {fetchDataNew} from "../../hook/axios.hook";
import {selectAllPositionPermissions, selectCurrentUserId} from "../../slices/UserSlice";
import REQUEST_ASSET from "../../utilities/assets/REQUEST";
import {calcCoStatusColor} from "../../utilities/calcStatusColor";
import {STATUS_COLORS} from "../../utilities/colorConstants";
import {decodeCurrencySymbol, formatNumberWithCurrency} from "../../utilities/currencyFormat";
import {dateFormat} from "../../utilities/dateFormat";
import {calcStatusColorViaPlannedDate} from "../../utilities/DateStatusCircle";
import {formName} from "../../utilities/formName";
import {CO_STATUSES, COF_STATES} from "../../utilities/generalConstants.js/generalConstants";
import usePagination from "../../utilities/hooks/usePagination";
import makeNameFromPersona from "../../utilities/makeNameFromPersona";
import parseNote from "../../utilities/parseNote";
import {fetchAllStockPools} from "../Logist/redux/operations";
import {selectIsLogistLoading} from "../Logist/redux/selectors";
import useIncomingReqTable from "../Logist/TransportPool/TPRequestDistribution/parts/IncomingReqTable/useIncomingReqTable";
import {calcProjectStatusColor} from "../Project/ProjectSecondVersion/ProjectPageSections/ProjectSection";
import {
  fetchAllCOs,
  fetchProjectsWithFilter,
  getCOLoading,
  getProjectLoading,
  getProjects,
  selectAllCOs,
} from "../Project/ProjectSlice";
import {fetchForUserApprovals} from "../Requests/Proform/redux/operations/ProformOperations";
import {selectProformsLoading, selectUserApprovals} from "../Requests/Proform/redux/selectors/ProformSelectors";
import {
  expandedTextArray,
  getExpandedDocs,
  profExpanded,
} from "../Requests/RequestsInProgress/utils/functions/expandDataForTable";
import {REQ_WINDOW_TYPES} from "../Requests/RequestsInProgress/utils/functions/requestsFiltersManager";
import {fetchRequests, getAllStatusRequest, getFirstStatusRequest, getLoadingRequest} from "../Requests/RequestsSlice";

const useMainPageSections = () => {
  const {
    i18n: {language},
  } = useTranslation();
  const dispatch = useDispatch();
  const currentUserId = useSelector(selectCurrentUserId);
  const allPositionPermissions = useSelector(selectAllPositionPermissions);

  //* Projects
  const areProjectsShown = allPositionPermissions?.[`/${API_ENDPOINTS.PROJECT.BASE}`]?.at(0) === "1";
  const projects = useSelector(getProjects);

  useEffect(() => {
    if (!currentUserId || !areProjectsShown) return;

    dispatch(fetchProjectsWithFilter({managerId: currentUserId}))
      .unwrap()
      .catch(err => console.error(err));
  }, [currentUserId, areProjectsShown]);

  const areProjectsLoading = useSelector(getProjectLoading);
  const projectsTableData = projects
    ?.filter(project => project?.statusId === 1)
    ?.map((project, i) => ({
      statusCircle: calcProjectStatusColor(project),
      number: i + 1,
      dateFrom: dateFormat(project.createdAt),
      dateTo: dateFormat(project?.contracts?.find(contract => contract.deadline)?.deadline),
      projectNumber: project?.projectNumber,
      address: project?.delivery?.address || "",
      planSum: formatNumberWithCurrency(project?.butget),
      factSum: formatNumberWithCurrency(project?.factButget),
      projectDesign: project.designNumber,
      projectManager: makeNameFromPersona({persona: project?.projectManager?.persona, language}) || "?",

      id: project.projectId,
    }));

  //* Active cos
  const areActiveCOsShown = allPositionPermissions?.[`/${API_ENDPOINTS.VED.REQUESTS_IN_PROGRESS}`]?.at(0) === "1";
  const allCOs = useSelector(selectAllCOs);

  const activeCOs = allCOs?.filter(({statusId}) => statusId && statusId < CO_STATUSES.DONE);
  const areActiveCOsLoading = useSelector(getCOLoading);

  const {changePage: changeActiveCOsPage, onPaginate: onActiveCOsPaginate, page: activeCOsPage} = usePagination();

  useEffect(() => {
    if (!currentUserId || !areActiveCOsShown || activeCOsPage.currentPage > activeCOsPage.totalPages) return;

    dispatch(fetchAllCOs({managerCoId: currentUserId, page: activeCOsPage?.currentPage, isCurrent: true}))
      .unwrap()
      .then(changeActiveCOsPage)
      .catch(err => console.error(err));
  }, [currentUserId, areActiveCOsShown, activeCOsPage?.currentPage]);

  const activeCOsTableData =
    (areActiveCOsShown &&
      Array.isArray(activeCOs) &&
      activeCOs?.map(
        ({
          coId,
          coNumber,
          startDate,
          finalDate,
          note,
          managerCo,
          project,
          statusId,
          bringTo,
          isDone,
          cofs,
          rejectedProforms,
          directives,
        }) => {
          const cofSecondNumbers = cofs
            ?.filter(({cofSecondNumber}) => cofSecondNumber)
            .map(({cofSecondNumber}) => cofSecondNumber)
            .join(", ");
          const hasOneFrozenCof = Array.isArray(cofs) && cofs?.length === 1 && cofs[0].freezedForClient;
          return {
            id: coId,
            status: calcCoStatusColor({startDate, bringTo, statusId, isDone}),
            // statusId >= 3 ? STATUS_COLORS.GREY : calcStatusColor({startDate, finalDate}),
            coNumber,
            clientNumber: "",
            projectManager: makeNameFromPersona({persona: project?.projectManager?.persona, language}),
            coManager: makeNameFromPersona({persona: managerCo?.persona, language}),
            cofSecondNumber: cofSecondNumbers?.includes(",")
              ? {text: cofSecondNumbers, expand: true}
              : cofSecondNumbers,
            note,
            startDate: dateFormat(startDate),
            finalDate: dateFormat(finalDate),
            isAccented: !!rejectedProforms?.length,
            state:
              directives?.length > 0 && +directives?.[0]?.sumFact !== 0
                ? COF_STATES.PAID
                : directives?.length > 0
                ? COF_STATES.HAS_DIRECTIVE
                : hasOneFrozenCof
                ? COF_STATES.FROZEN
                : "",
          };
        },
      )) ||
    [];

  //* Incoming requests
  const areIncomingRequestsShown =
    allPositionPermissions?.[`/${API_ENDPOINTS.VED.BOSS_REQUEST_REGISTER}`]?.at(0) === "1";
  const requests = useSelector(getFirstStatusRequest);
  const areIncomingRequestsLoading = useSelector(getLoadingRequest);

  useEffect(() => {
    if (!areIncomingRequestsShown) return;

    dispatch(fetchRequests({statusId: 1, window: "Incoming requests", pageSize: 250}))
      .unwrap()
      .catch(err => console.error(err));
  }, [areIncomingRequestsShown]);

  const transformReqForTable = (item, i) => {
    const {requestId, status, factory, managerCo, cos, createdAt, productGroups, ...rest} = item;

    const closerBringTo = cos.reduce((prev, current) => {
      const prevDate = prev.bringTo;
      const currentDate = current.bringTo;
      return prevDate > currentDate;
    });

    function compositeProductGroupNames(productGroups) {
      if (!productGroups) return "";
      return productGroups.reduce(
        (acc, productGroup) => (acc === "" ? productGroup.name : acc + ",  " + productGroup.name),
        "",
      );
    }

    return {
      ...rest,
      number: i + 1,
      status: formName({...status, language}),
      managerCo: makeNameFromPersona({persona: managerCo?.persona, language}) || managerCo?.name || "",
      factory: factory?.name || "",
      coNumber: cos[0]?.coNumber || "",
      id: requestId,
      createdAt: dateFormat(createdAt),
      updatedAt: dateFormat(closerBringTo),
      noname: "",
      productGroup: compositeProductGroupNames(productGroups),
      country: cos[0]?.project?.delivery?.country?.shortName,
      bringTo: dateFormat(cos[0]?.bringTo),
    };
  };
  const incomingReqTableData =
    (areIncomingRequestsShown && Array.isArray(requests) && requests.map(transformReqForTable)) || [];

  //* Control PF
  const areControlPfShown = allPositionPermissions?.[`/${API_ENDPOINTS.CO.CONTROL}`]?.at(0) === "1";
  const approvals = useSelector(selectUserApprovals);
  const isControlPfLoading = useSelector(selectProformsLoading);

  useEffect(() => {
    if (!currentUserId || !areControlPfShown) return;

    dispatch(fetchForUserApprovals(currentUserId))
      .unwrap()
      .catch(err => console.error(err));
  }, [currentUserId, areControlPfShown]);

  const circleColor = appr => {
    switch (true) {
      case !appr?.isApproved && !appr?.isDeclined:
        return STATUS_COLORS.GREY;
      case appr?.isApproved:
        return STATUS_COLORS.GREEN;
      default:
        return STATUS_COLORS.RED;
    }
  };

  const controlPfTableData =
    (areControlPfShown &&
      Array.isArray(approvals) &&
      approvals
        ?.filter(appr => appr?.proforma?.request && !appr?.isApproved && !appr?.isDeclined)
        ?.map((appr, i) => ({
          id: appr?.approvalId,
          number: i + 1,
          pfNumber: appr?.proforma?.proformaNumber || "",
          coNumber:
            appr?.proforma?.request?.orders?.length > 1
              ? {
                  text: appr?.proforma?.request?.orders?.map(orders => orders?.co?.coNumber).join(", "),
                  expand: true,
                }
              : appr?.proforma?.request?.orders?.length === 0
              ? ""
              : appr?.proforma?.request?.orders?.[0].co?.coNumber,
          requestNumber: appr?.proforma?.request?.requestNumber || "",
          noteFEA: appr?.proforma?.note || "",
          managerFEA: makeNameFromPersona({persona: appr?.proforma?.request?.managerFea?.persona, language}),
          status: circleColor(appr),
        }))) ||
    [];

  //* FEA Requests in progress
  const areFeaReqInProgressShown =
    allPositionPermissions?.[`/${API_ENDPOINTS.VED.REQUESTS_IN_PROGRESS}`]?.at(0) === "1";
  const feaReqInProgress = useSelector(getAllStatusRequest);
  const isFeaReqInProgressLoading = useSelector(getLoadingRequest);

  const {
    changePage: changeFeaReqInProgressPage,
    onPaginate: onFeaReqInProgressPaginate,
    page: feaReqInProgressPage,
  } = usePagination();

  useEffect(() => {
    if (
      !currentUserId ||
      !areFeaReqInProgressShown ||
      feaReqInProgressPage.currentPage > feaReqInProgressPage.totalPages
    )
      return;

    dispatch(
      fetchRequests({
        window: REQ_WINDOW_TYPES.INPROGRESS,
        managerFeaId: currentUserId,
        page: feaReqInProgressPage?.currentPage || 1,
      }),
    )
      .unwrap()
      .then(changeFeaReqInProgressPage)
      .catch(err => console.error(err));
  }, [currentUserId, areFeaReqInProgressShown, feaReqInProgressPage?.currentPage]);

  const feaReqInProgressTableData =
    (areFeaReqInProgressShown &&
      Array.isArray(feaReqInProgress) &&
      feaReqInProgress?.map((request, index) => {
        const {
          requestId,
          status,
          factory,
          factory: {contacts} = {},
          managerFea,
          createdAt,
          requestDates,
          cos,
          proforms,
          transportDocs,
          currency,
          requestSum,
          note,
          ...rest
        } = request;

        const requestAsset = new REQUEST_ASSET(request);
        const statusName = status?.[language === "en" ? "engName" : "ukrName"] || "";
        const lastDate = requestDates?.reduce((prev, current) => {
          return prev.dateId > current.dateId ? prev : current;
        });

        const closestCOByBringTo = Array.isArray(cos)
          ? cos.reduce((prev, current) => {
              if (!prev) return current;
              if (!prev?.bringTo) return current;
              if (!current?.bringTo) return prev;

              return new Date(prev.bringTo) <= new Date(current.bringTo) ? prev : current;
            }, null)
          : null;

        const bringToDates = Array.isArray(cos)
          ? cos
              .map(co => dateFormat(co?.bringTo))
              .filter(Boolean)
              .filter(v => v !== " - ")
          : [];
        const additionalNumber = Array.isArray(cos)
          ? cos?.map(co => co.cofs?.[0]?.cofSecondNumber).filter(Boolean)
          : [];

        const unickManagersCO = Array.from(
          new Map(
            cos?.map(obj => [obj.managerCo.userId, makeNameFromPersona({persona: obj.managerCo?.persona, language})]),
          ).values(),
        );

        const color = calcStatusColorViaPlannedDate(lastDate?.plannedDate, lastDate?.createdAt, lastDate?.actualDate);

        const totalProfSum = Array.isArray(proforms)
          ? proforms.reduce((acc, p) => acc + (parseFloat(p?.proformaSum) || 0), 0)
          : 0;
        const rawSymbol = currency?.symbol || proforms?.[0]?.currency?.symbol || "";
        const decodedSymbol = decodeCurrencySymbol(rawSymbol);
        const fallbackShortName = currency?.shortName || proforms?.[0]?.currency?.shortName || "";
        const currencySymbol = decodedSymbol || fallbackShortName;

        const payedSum = requestAsset.payedSum;

        return {
          ...rest,
          number: index + 1,
          prof: profExpanded(proforms),
          requestStatus: statusName,
          managerCo: unickManagersCO.join(", "),
          managerFea: makeNameFromPersona({persona: managerFea?.persona, language}) || "",
          factory: factory?.name || (contacts && contacts?.[0]?.name) || "",
          id: requestId,
          createdAt: dateFormat(createdAt),
          updatedAt: dateFormat(closestCOByBringTo?.bringTo),
          noname: "",
          statusCircle: color,
          country: cos?.[0]?.project?.delivery?.country?.shortName,
          bringTo: expandedTextArray(bringToDates),
          packL: getExpandedDocs(transportDocs, 1),
          invoice: getExpandedDocs(transportDocs, 2),
          requestSum: requestSum ? formatNumberWithCurrency(requestSum) : "",
          profSum: totalProfSum ? formatNumberWithCurrency(totalProfSum, currencySymbol) : "",
          payedSum: payedSum ? formatNumberWithCurrency(payedSum, currencySymbol) : " - ",
          stockPool: request?.stockPool?.stockPoolNumber || request?.stockPools?.[0]?.stockPoolNumber || "",
          currencyLabel: currencySymbol,
          cofSecondNumber: expandedTextArray(additionalNumber),
          note: parseNote(note),
        };
      })) ||
    [];

  //* FEA ready status requests
  const areFeaReadyStatusReqShown =
    allPositionPermissions?.[`/${API_ENDPOINTS.VED.READY_STATUS_REQUESTS}`]?.at(0) === "1";

  const [feaReadyStatusReq, setFeaReadyStatusReq] = useState([]);

  const areReadyStatusReqLoading = useSelector(getLoadingRequest);

  const {
    changePage: changeReadyStatusReqPage,
    onPaginate: onReadyStatusReqPaginate,
    page: feaReadyStatusReqPage,
  } = usePagination();

  useEffect(() => {
    if (
      !currentUserId ||
      !areFeaReadyStatusReqShown ||
      feaReadyStatusReqPage.currentPage > feaReadyStatusReqPage.totalPages
    )
      return;

    fetchDataNew(`/requests/getAll/`, "get", {
      params: {
        window: REQ_WINDOW_TYPES.PAID,
        managerFeaId: currentUserId,
        page: feaReadyStatusReqPage?.currentPage || 1,
      },
    })
      .then(resp => {
        changeReadyStatusReqPage(resp);
        setFeaReadyStatusReq(prev => [...(prev || []), ...(resp?.data?.requests || [])]);
      })
      .catch(err => console.error(err));
  }, [currentUserId, areFeaReadyStatusReqShown, feaReadyStatusReqPage?.currentPage]);

  const feaReadyStatusReqTableData =
    (areFeaReadyStatusReqShown &&
      Array.isArray(feaReadyStatusReq) &&
      feaReadyStatusReq?.map((request, index) => {
        const {
          requestId,
          status,
          factory,
          factory: {contacts} = {},
          managerFea,
          createdAt,
          requestDates,
          cos,
          proforms,
          transportDocs,
          currency,
          requestSum,
          note,
          ...rest
        } = request;

        const requestAsset = new REQUEST_ASSET(request);
        const statusName = status?.[language === "en" ? "engName" : "ukrName"] || "";
        const lastDate = requestDates?.reduce((prev, current) => {
          return prev.dateId > current.dateId ? prev : current;
        });

        const closestCOByBringTo = Array.isArray(cos)
          ? cos.reduce((prev, current) => {
              if (!prev) return current;
              if (!prev?.bringTo) return current;
              if (!current?.bringTo) return prev;

              return new Date(prev.bringTo) <= new Date(current.bringTo) ? prev : current;
            }, null)
          : null;

        const bringToDates = Array.isArray(cos)
          ? cos
              .map(co => dateFormat(co?.bringTo))
              .filter(Boolean)
              .filter(v => v !== " - ")
          : [];
        const additionalNumber = Array.isArray(cos)
          ? cos?.map(co => co.cofs?.[0]?.cofSecondNumber).filter(Boolean)
          : [];

        const unickManagersCO = Array.from(
          new Map(
            cos?.map(obj => [obj.managerCo.userId, makeNameFromPersona({persona: obj.managerCo?.persona, language})]),
          ).values(),
        );

        const color = calcStatusColorViaPlannedDate(lastDate?.plannedDate, lastDate?.createdAt, lastDate?.actualDate);

        const totalProfSum = Array.isArray(proforms)
          ? proforms.reduce((acc, p) => acc + (parseFloat(p?.proformaSum) || 0), 0)
          : 0;
        const rawSymbol = currency?.symbol || proforms?.[0]?.currency?.symbol || "";
        const decodedSymbol = decodeCurrencySymbol(rawSymbol);
        const fallbackShortName = currency?.shortName || proforms?.[0]?.currency?.shortName || "";
        const currencySymbol = decodedSymbol || fallbackShortName;

        const payedSum = requestAsset.payedSum;

        return {
          ...rest,
          number: index + 1,
          prof: profExpanded(proforms),
          requestStatus: statusName,
          managerCo: unickManagersCO.join(", "),
          managerFea: makeNameFromPersona({persona: managerFea?.persona, language}) || "",
          factory: factory?.name || (contacts && contacts?.[0]?.name) || "",
          id: requestId,
          createdAt: dateFormat(createdAt),
          updatedAt: dateFormat(closestCOByBringTo?.bringTo),
          noname: "",
          statusCircle: color,
          country: cos?.[0]?.project?.delivery?.country?.shortName,
          bringTo: expandedTextArray(bringToDates),
          packL: getExpandedDocs(transportDocs, 1),
          invoice: getExpandedDocs(transportDocs, 2),
          requestSum: requestSum ? formatNumberWithCurrency(requestSum) : "",
          profSum: totalProfSum ? formatNumberWithCurrency(totalProfSum, currencySymbol) : "",
          payedSum: payedSum ? formatNumberWithCurrency(payedSum, currencySymbol) : " - ",
          stockPool: request?.stockPool?.stockPoolNumber || request?.stockPools?.[0]?.stockPoolNumber || "",
          currencyLabel: currencySymbol,
          cofSecondNumber: expandedTextArray(additionalNumber),
          note: parseNote(note),
        };
      })) ||
    [];

  //* Requests for distribution Logist
  const areReqForDistributionLogShown =
    allPositionPermissions?.[`/${API_ENDPOINTS.LOGIST.REQUESTS_REGISTER_BOSS}`]?.at(0) === "1";
  const areReqForDistributionLogLoading = useSelector(selectIsLogistLoading);

  useEffect(() => {
    if (!areReqForDistributionLogShown) return;

    dispatch(fetchAllStockPools({window: "Incoming"}))
      .unwrap()
      .catch(err => console.error(err));
  }, [areReqForDistributionLogShown]);

  const {defaultData: reqForDistributionLogTableData} = useIncomingReqTable({
    selectedSP: null,
  });

  return {
    projectsTableData,
    areProjectsShown,
    areProjectsLoading,
    areActiveCOsShown,
    areActiveCOsLoading,
    activeCOsTableData,
    onActiveCOsPaginate,
    activeCOsPage,
    controlPfTableData,
    areControlPfShown,
    isControlPfLoading,
    incomingReqTableData,
    areIncomingRequestsShown,
    areIncomingRequestsLoading,
    feaReqInProgressTableData,
    areFeaReqInProgressShown,
    isFeaReqInProgressLoading,
    onFeaReqInProgressPaginate,
    feaReqInProgressPage,
    feaReadyStatusReqTableData,
    areFeaReadyStatusReqShown,
    areReadyStatusReqLoading,
    onReadyStatusReqPaginate,
    feaReadyStatusReqPage,
    reqForDistributionLogTableData,
    areReqForDistributionLogShown,
    areReqForDistributionLogLoading,
  };
};

export default useMainPageSections;
